import React, { useState, useContext, useEffect, useMemo } from "react";
import { UserContext } from "../../Context/UserContext";
import OpportunityService from "../../api/opportunityService";
import Req_Rec_Card from "./Req_Rec_Card";
import { CircularProgress } from "@material-ui/core";
import CustomDateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import { DropDownMUI } from "../../components";
import { FaCalendarAlt, FaChevronRight, FaChevronDown } from "react-icons/fa";
import "./Req_Rec_Card.scss";

/**
 * ENHANCED FINANCE ANALYTICS WITH DUPLICATE PREVENTION
 * =====================================================
 *
 * This component implements sophisticated logic to prevent counting the same order
 * multiple times across different finance requests. The key improvements include:
 *
 * 1. ORDER IDENTIFICATION:
 *    - Creates unique identifiers for orders using OrderId, uuid, id, or fallback hash
 *    - Ensures the same order is recognized across different requests
 *
 * 2. FIRST REQUEST DATE TRACKING:
 *    - Identifies the earliest request date for each order from History data
 *    - Only counts the order amount on the date of the first request
 *
 * 3. CONTEXT-AWARE DUPLICATE PREVENTION:
 *    - Uses scoped tracking within each calculation context
 *    - Prevents double-counting within date groups, time ranges, and project summaries
 *
 * 4. BUSINESS LOGIC COMPLIANCE:
 *    - Maintains existing timestamp threshold logic (1747699200000)
 *    - Respects firstRequest flags for newer entries
 *    - Applies general logic for older entries while still preventing duplicates
 *
 * 5. CALCULATION CONSISTENCY:
 *    - All total calculations use the same duplicate prevention logic
 *    - Ensures accuracy across daily, weekly, monthly views
 *    - Maintains consistency between different groupings (date, project, etc.)
 *
 * Example Scenario:
 * - Order #12345 has 3 finance requests on different dates
 * - First request: Jan 1st, Second: Jan 5th, Third: Jan 10th
 * - Order amount will only be counted on Jan 1st (first request date)
 * - Subsequent requests on Jan 5th and Jan 10th won't add to the total
 * - This ensures accurate financial reporting without inflated totals
 */

// Helper function to check if a date lies within a range
const isDateLiesCheck = (currentItemDate, DateRangeValue) => {
  if (!DateRangeValue || !DateRangeValue[0] || !DateRangeValue[1]) return true;

  if (
    new Date(currentItemDate).getTime() >=
      new Date(DateRangeValue[0]).getTime() &&
    new Date(currentItemDate).getTime() <= new Date(DateRangeValue[1]).getTime()
  ) {
    return true;
  }
  return false;
};

// Helper function to check if item is a first request
const isFirstRequestItem = (item) => {
  try {
    const parsedHistory = JSON.parse(item.History || "{}");
    const firstHistoryEntry = Object.values(parsedHistory)?.[0];
    return firstHistoryEntry?.firstRequest === true;
  } catch {
    return false;
  }
};

// Timestamp threshold for business logic
const timestampThreshold = 1747699200000;

// Global order tracking registry - ensures orders are never counted more than once
const globalOrderRegistry = {
  countedOrders: new Set(),
  orderFirstRequestMap: new Map(),
  isInitialized: false,

  // Initialize the registry with all finance data
  initialize(allFinanceData) {
    this.countedOrders.clear();
    this.orderFirstRequestMap.clear();

    // First pass: identify all unique orders and their first request dates
    const orderMap = new Map();

    allFinanceData.forEach((item) => {
      // Only process requests, not records
      if (item.type !== "request") return;

      const orderId = getOrderIdentifier(item);
      const firstRequestDate = getFirstRequestDate(item);

      if (!orderMap.has(orderId)) {
        orderMap.set(orderId, {
          firstRequestDate,
          amount: parseFloat(item.OrderTotalAmount || 0),
          firstRequestItem: item,
        });
      } else {
        // Update with the earliest first request date
        const existing = orderMap.get(orderId);
        if (
          firstRequestDate &&
          (!existing.firstRequestDate ||
            firstRequestDate < existing.firstRequestDate)
        ) {
          orderMap.set(orderId, {
            firstRequestDate,
            amount: parseFloat(item.OrderTotalAmount || 0),
            firstRequestItem: item,
          });
        }
      }
    });

    // Second pass: determine which specific requests should contribute to totals
    allFinanceData.forEach((item) => {
      if (item.type !== "request") return;

      const orderId = getOrderIdentifier(item);
      const orderData = orderMap.get(orderId);
      if (!orderData) return;

      let parsedHistory;
      try {
        parsedHistory = JSON.parse(item.History || "{}");
      } catch {
        parsedHistory = {};
      }

      const firstHistoryEntry = Object.values(parsedHistory)?.[0];
      const entryTime = firstHistoryEntry?.date;

      // Check if this specific item represents the first request for this order
      const isThisTheFirstRequest = orderData.firstRequestDate === entryTime;

      if (isThisTheFirstRequest) {
        // Determine if this order should be counted based on business logic
        let shouldCount = false;

        if (entryTime > timestampThreshold) {
          // Use filtered logic: only if firstRequest === true
          const isFirstRequest = firstHistoryEntry?.firstRequest === true;
          shouldCount = isFirstRequest;
        } else {
          // Use general logic for older entries
          shouldCount = true;
        }

        if (shouldCount) {
          const amount = parseFloat(item.OrderTotalAmount || 0);
          if (!isNaN(amount) && amount > 0) {
            // Map this specific request item to be the counting instance for this order
            this.orderFirstRequestMap.set(orderId, {
              item: item,
              amount: amount,
              shouldCount: true,
            });
          }
        }
      }
    });

    this.isInitialized = true;
    console.log("🔧 Global Order Registry Initialized:", {
      totalUniqueOrders: orderMap.size,
      ordersToCount: this.orderFirstRequestMap.size,
      countedOrders: Array.from(this.orderFirstRequestMap.keys()),
    });
  },

  // Check if a specific request should contribute to the total
  shouldCountOrder(item) {
    if (!this.isInitialized) {
      console.warn("⚠️ Global Order Registry not initialized");
      return false;
    }

    const orderId = getOrderIdentifier(item);
    const orderData = this.orderFirstRequestMap.get(orderId);

    // Only count if this is the designated counting instance for this order
    return orderData && orderData.item === item && orderData.shouldCount;
  },

  // Get the amount for an order (if it should be counted)
  getOrderAmount(item) {
    if (!this.shouldCountOrder(item)) return 0;

    const orderId = getOrderIdentifier(item);
    const orderData = this.orderFirstRequestMap.get(orderId);
    return orderData ? orderData.amount : 0;
  },

  // Reset the registry
  reset() {
    this.countedOrders.clear();
    this.orderFirstRequestMap.clear();
    this.isInitialized = false;
  },
};

// Helper function to get unique order identifier
const getOrderIdentifier = (item) => {
  // Try multiple fields to create a unique identifier for the order
  if (item.OrderId) return `order_${item.OrderId}`;
  if (item.uuid) return `uuid_${item.uuid}`;
  if (item.id) return `id_${item.id}`;

  // Fallback: create identifier from order details
  const orderDetails = [
    item.OrderTotalAmount,
    item.ProjectDetails,
    item.VendorDetails,
  ]
    .filter(Boolean)
    .join("_");

  return `fallback_${orderDetails}`;
};

// Helper function to get the first request date for an order
const getFirstRequestDate = (item) => {
  try {
    const parsedHistory = JSON.parse(item.History || "{}");
    const historyEntries = Object.values(parsedHistory);

    if (historyEntries.length === 0) return null;

    // Sort by date to find the earliest entry
    const sortedEntries = historyEntries.sort(
      (a, b) => (a.date || 0) - (b.date || 0)
    );
    return sortedEntries[0]?.date || null;
  } catch {
    return null;
  }
};

// Enhanced calculate total amount using global order tracking
const calculateOrderAmountWithBusinessLogic = (items) => {
  if (!globalOrderRegistry.isInitialized) {
    console.warn(
      "⚠️ Attempting calculation before global registry initialization"
    );
    return 0;
  }

  return items.reduce((total, item) => {
    // Only calculate for requests, not records
    if (item.type !== "request") return total;

    // Use global registry to determine if this order should be counted
    const amount = globalOrderRegistry.getOrderAmount(item);
    return total + amount;
  }, 0);
};

const TimeBubbles = ({ viewMode, setViewMode }) => {
  const colors = {
    primary: "#fdd34d",
    hover: "#003d7a",
    inactive: "#f8f9fa",
    border: "#dee2e6",
    text: "#212529",
    textLight: "#6c757d",
    textActive: "#353535",
  };

  const containerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.5rem",
    borderRadius: "12px",
    backgroundColor: "#ffffff",
    width: "fit-content",
  };

  const bubbleBaseStyle = {
    padding: "0.625rem 1.25rem",
    margin: "0 0.25rem",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontFamily:
      "SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, sans-serif",
    fontWeight: "500",
    fontSize: "0.875rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "5rem",
  };

  const activeBubbleStyle = {
    ...bubbleBaseStyle,
    backgroundColor: colors.primary,
    color: colors.textActive,
    boxShadow: "0 2px 8px rgba(0, 86, 179, 0.25)",
  };

  const inactiveBubbleStyle = {
    ...bubbleBaseStyle,
    backgroundColor: colors.inactive,
    color: colors.textLight,
    border: `1px solid ${colors.border}`,
  };

  const handleSetViewMode = (mode) => {
    setViewMode(mode);
  };

  return (
    <div style={containerStyle}>
      <div
        style={viewMode === "daily" ? activeBubbleStyle : inactiveBubbleStyle}
        onClick={() => handleSetViewMode("daily")}
        onMouseOver={(e) => {
          if (viewMode !== "daily") {
            e.currentTarget.style.backgroundColor = "#f1f3f5";
          }
        }}
        onMouseOut={(e) => {
          if (viewMode !== "daily") {
            e.currentTarget.style.backgroundColor = colors.inactive;
          }
        }}
      >
        Daily
      </div>
      <div
        style={viewMode === "weekly" ? activeBubbleStyle : inactiveBubbleStyle}
        onClick={() => handleSetViewMode("weekly")}
        onMouseOver={(e) => {
          if (viewMode !== "weekly") {
            e.currentTarget.style.backgroundColor = "#f1f3f5";
          }
        }}
        onMouseOut={(e) => {
          if (viewMode !== "weekly") {
            e.currentTarget.style.backgroundColor = colors.inactive;
          }
        }}
      >
        Weekly
      </div>
      <div
        style={viewMode === "monthly" ? activeBubbleStyle : inactiveBubbleStyle}
        onClick={() => handleSetViewMode("monthly")}
        onMouseOver={(e) => {
          if (viewMode !== "monthly") {
            e.currentTarget.style.backgroundColor = "#f1f3f5";
          }
        }}
        onMouseOut={(e) => {
          if (viewMode !== "monthly") {
            e.currentTarget.style.backgroundColor = colors.inactive;
          }
        }}
      >
        Monthly
      </div>
    </div>
  );
};

const DateGroup = ({
  date,
  items,
  isOpen,
  toggleOpen,
  setAllFinanceData,
  setRawData,
  selectedRequests,
  handleCheckboxChange,
}) => {
  const getProjectColor = (name) => {
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEAA7",
      "#DDA0DD",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E9",
    ];
    const hash = name.split("").reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  const getShortDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year:
          date.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
      });
    }
  };

  // Calculate summary for this date
  const summary = useMemo(() => {
    // Calculate total using business logic (OrderTotalAmount with timestamp threshold)
    const total = calculateOrderAmountWithBusinessLogic(items);

    let approved = 0;
    let pending = 0;
    let approvedCount = 0;
    let pendingCount = 0;
    let requestCount = 0;
    let recordCount = 0;

    items.forEach((item) => {
      if (item.type === "request") {
        requestCount++;
        const amount = Number(item.AmountRequested || 0);

        if (item.status === "Approved") {
          approved += amount;
          approvedCount++;
        } else if (item.status === "Request" || item.status === "Requested") {
          pending += amount;
          pendingCount++;
        }
      } else if (item.type === "record") {
        recordCount++;
        const amount = Number(item.AmountPaid || 0);
        approved += amount;
        approvedCount++;
      }
    });

    return {
      total,
      approved,
      pending,
      approvedCount,
      pendingCount,
      requestCount,
      recordCount,
    };
  }, [items]);

  return (
    <div style={{ marginBottom: "16px", width: "100%" }}>
      {/* Date Group Header */}
      <div
        onClick={toggleOpen}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          cursor: "pointer",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
          transition: "all 0.2s ease",
          marginBottom: isOpen ? "12px" : "0",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
          e.currentTarget.style.borderColor = "#fdd34d";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.05)";
          e.currentTarget.style.borderColor = "#e5e7eb";
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FaCalendarAlt style={{ color: "#6b7280", fontSize: "14px" }} />
            <span
              style={{ fontWeight: "600", fontSize: "16px", color: "#111827" }}
            >
              {getShortDate(date)}
            </span>
            <span style={{ fontSize: "14px", color: "#6b7280" }}>
              {new Date(date).toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          </div>
          <div style={{ fontSize: "12px", color: "#6b7280" }}>
            ({summary.requestCount} requests, {summary.recordCount} records)
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {/* Summary badges */}
          {/* <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div style={{
              padding: "4px 8px",
              backgroundColor: "#dcfce7",
              color: "#166534",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: "500"
            }}>
              Approved ({summary.approvedCount}): ₹{summary.approved.toLocaleString('en-IN')}
            </div>
            {summary.pending > 0 && (
              <div style={{
                padding: "4px 8px",
                backgroundColor: "#fef3c7",
                color: "#92400e",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "500"
              }}>
                Pending ({summary.pendingCount}): ₹{summary.pending.toLocaleString('en-IN')}
              </div>
            )}
          </div> */}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "#fdd34d",
              padding: "8px 12px",
              borderRadius: "8px",
              fontWeight: "600",
              color: "#333",
            }}
          >
            <span style={{ fontSize: "14px" }}>Total Amount:</span>
            <span style={{ fontSize: "16px" }}>
              ₹
              {summary.total.toLocaleString("en-IN", {
                maximumFractionDigits: 0,
              })}
            </span>
          </div>

          <div
            style={{
              transition: "transform 0.3s ease",
              transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
              color: "#6b7280",
            }}
          >
            <FaChevronRight size={14} />
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      {isOpen && (
        <div
          style={{
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "16px",
            animation: "fadeIn 0.3s ease-in-out",
          }}
        >
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; transform: translateY(-10px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}
          </style>

          {items.map((item, index) => {
            const isFirstRequest = isFirstRequestItem(item);
            const shouldCountThisOrder =
              globalOrderRegistry.shouldCountOrder(item);
            return (
              <div
                key={`${item.uuid || item.id}-${index}`}
                style={{
                  position: "relative",
                  marginBottom: index < items.length - 1 ? "12px" : "0",
                  ...(isFirstRequest && {
                    border: "2px solid #fbbf24",
                    borderRadius: "12px",
                    backgroundColor: "#fffbeb",
                    padding: "8px",
                    margin: "4px 0",
                    boxShadow: "0 2px 8px rgba(251, 191, 36, 0.15)",
                  }),
                  ...(!shouldCountThisOrder &&
                    item.type === "request" && {
                      border: "2px solid #ef4444",
                      borderRadius: "12px",
                      backgroundColor: "#fef2f2",
                      opacity: 0.7,
                      padding: "8px",
                      margin: "4px 0",
                    }),
                }}
              >
                {isFirstRequest && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-8px",
                      right: "8px",
                      backgroundColor: "#fbbf24",
                      color: "#78350f",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "10px",
                      fontWeight: "600",
                      zIndex: 1,
                    }}
                  >
                    FIRST REQUEST
                  </div>
                )}
                {!shouldCountThisOrder && item.type === "request" && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-8px",
                      left: "8px",
                      backgroundColor: "#ef4444",
                      color: "white",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "10px",
                      fontWeight: "600",
                      zIndex: 1,
                    }}
                  >
                    {/* NOT COUNTED (DUPLICATE ORDER) */}
                  </div>
                )}
                <Req_Rec_Card
                  request={item}
                  rIndex={index}
                  setFinRequestsData={setAllFinanceData}
                  isRecord={item.type === "record"}
                  setRawData={setRawData}
                  selectedRequests={selectedRequests}
                  handleCheckboxChange={handleCheckboxChange}
                  className="finance-analytics-card"
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TimeGroupHeader = ({
  dateRange,
  isOpen,
  toggleOpen,
  summary = {
    total: 0,
    approved: 0,
    pending: 0,
    declined: 0,
    requestCount: 0,
    recordCount: 0,
  },
  projects = [],
  projectGroups,
}) => {
  const formatDateRange = (range) => {
    const [start, end] = range;
    const startDate = new Date(start);
    const endDate = new Date(end);

    const options = {
      day: "numeric",
      month: "short",
      year: "numeric",
    };

    return `${startDate.toLocaleDateString(
      "en-US",
      options
    )} - ${endDate.toLocaleDateString("en-US", options)}`;
  };

  const getProjectColor = (name) => {
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEAA7",
      "#DDA0DD",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E9",
    ];
    const hash = name.split("").reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 20px",
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        cursor: "pointer",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
        transition: "all 0.2s ease",
        marginBottom: isOpen ? "12px" : "16px",
      }}
      onClick={toggleOpen}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
        e.currentTarget.style.borderColor = "#fdd34d";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.05)";
        e.currentTarget.style.borderColor = "#e5e7eb";
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <FaCalendarAlt style={{ color: "#6b7280", fontSize: "14px" }} />
          <span
            style={{ fontWeight: "600", fontSize: "16px", color: "#111827" }}
          >
            {formatDateRange(dateRange)}
          </span>
        </div>
        <div style={{ fontSize: "12px", color: "#6b7280" }}>
          ({summary.requestCount} requests, {summary.recordCount} records)
        </div>

        {/* Project bubbles */}
        <div
          style={{
            display: "flex",
            gap: "4px",
            flexWrap: "wrap",
            maxWidth: "300px",
          }}
        >
          {projects.slice(0, 3).map((project) => (
            <div
              key={project}
              style={{
                backgroundColor: getProjectColor(project),
                color: "white",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "10px",
                fontWeight: "500",
                whiteSpace: "nowrap",
              }}
            >
              {project.length > 10 ? `${project.substring(0, 10)}...` : project}
            </div>
          ))}
          {projects.length > 3 && (
            <div
              style={{
                backgroundColor: "#6b7280",
                color: "white",
                padding: "2px 6px",
                borderRadius: "4px",
                fontSize: "10px",
                fontWeight: "500",
              }}
            >
              +{projects.length - 3}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        {/* <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <div
            style={{
              padding: "4px 8px",
              backgroundColor: "#dcfce7",
              color: "#166534",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            Approved: ₹{summary.approved.toLocaleString("en-IN")}
          </div>
          {summary.pending > 0 && (
            <div
              style={{
                padding: "4px 8px",
                backgroundColor: "#fef3c7",
                color: "#92400e",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "500",
              }}
            >
              Pending: ₹{summary.pending.toLocaleString("en-IN")}
            </div>
          )}
        </div> */}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: "#fdd34d",
            padding: "8px 12px",
            borderRadius: "8px",
            fontWeight: "600",
            color: "#333",
          }}
        >
          <span style={{ fontSize: "14px" }}>Total:</span>
          <span style={{ fontSize: "16px" }}>
            ₹
            {summary.total.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </span>
        </div>

        <div
          style={{
            transition: "transform 0.3s ease",
            transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
            color: "#6b7280",
          }}
        >
          <FaChevronRight size={14} />
        </div>
      </div>
    </div>
  );
};

const ProjectGroup = ({
  projectName,
  projectItems,
  isOpen,
  toggleOpen,
  setAllFinanceData,
  setRawData,
  selectedRequests,
  handleCheckboxChange,
  summary = {
    total: 0,
    approved: 0,
    pending: 0,
    approvedCount: 0,
    pendingCount: 0,
    requestCount: 0,
    recordCount: 0,
  },
}) => {
  const getProjectInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : "P";
  };

  const getProjectColor = (name) => {
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEAA7",
      "#DDA0DD",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E9",
    ];
    const hash = name.split("").reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div style={{ marginBottom: "12px", marginLeft: "20px" }}>
      {/* Project Header */}
      <div
        onClick={toggleOpen}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          backgroundColor: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          cursor: "pointer",
          transition: "all 0.2s ease",
          marginBottom: isOpen ? "8px" : "0",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#f1f5f9";
          e.currentTarget.style.borderColor = "#fdd34d";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "#f8fafc";
          e.currentTarget.style.borderColor = "#e2e8f0";
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: getProjectColor(projectName),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            {getProjectInitial(projectName)}
          </div>
          <div>
            <div
              style={{ fontWeight: "600", fontSize: "14px", color: "#334155" }}
            >
              {projectName}
            </div>
            <div style={{ fontSize: "12px", color: "#64748b" }}>
              {summary.requestCount} requests, {summary.recordCount} records
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ display: "flex", gap: "6px" }}>
            <div
              style={{
                padding: "3px 6px",
                backgroundColor: "#dcfce7",
                color: "#166534",
                borderRadius: "4px",
                fontSize: "11px",
                fontWeight: "500",
              }}
            >
              ₹{summary.approved.toLocaleString("en-IN")}
            </div>
            {summary.pending > 0 && (
              <div
                style={{
                  padding: "3px 6px",
                  backgroundColor: "#fef3c7",
                  color: "#92400e",
                  borderRadius: "4px",
                  fontSize: "11px",
                  fontWeight: "500",
                }}
              >
                ₹{summary.pending.toLocaleString("en-IN")}
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontWeight: "600",
              fontSize: "14px",
              color: "#333",
            }}
          >
            <span>₹{summary.total.toLocaleString("en-IN")}</span>
          </div>

          <div
            style={{
              transition: "transform 0.3s ease",
              transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
              color: "#64748b",
            }}
          >
            <FaChevronRight size={12} />
          </div>
        </div>
      </div>

      {/* Project Items */}
      {isOpen && (
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "12px",
            marginLeft: "20px",
          }}
        >
          {projectItems.map((item, index) => {
            const isFirstRequest = isFirstRequestItem(item);
            const shouldCountThisOrder =
              globalOrderRegistry.shouldCountOrder(item);
            return (
              <div
                key={`${item.uuid || item.id}-${index}`}
                style={{
                  position: "relative",
                  marginBottom: index < projectItems.length - 1 ? "8px" : "0",
                  ...(isFirstRequest && {
                    border: "2px solid #fbbf24",
                    borderRadius: "12px",
                    backgroundColor: "#fffbeb",
                    padding: "8px",
                    margin: "4px 0",
                    boxShadow: "0 2px 8px rgba(251, 191, 36, 0.15)",
                  }),
                  ...(!shouldCountThisOrder &&
                    item.type === "request" && {
                      border: "2px solid #ef4444",
                      borderRadius: "12px",
                      backgroundColor: "#fef2f2",
                      opacity: 0.7,
                      padding: "8px",
                      margin: "4px 0",
                    }),
                }}
              >
                {isFirstRequest && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-8px",
                      right: "8px",
                      backgroundColor: "#fbbf24",
                      color: "#78350f",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "10px",
                      fontWeight: "600",
                      zIndex: 1,
                    }}
                  >
                    FIRST REQUEST
                  </div>
                )}
                {!shouldCountThisOrder && item.type === "request" && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-8px",
                      left: "8px",
                      backgroundColor: "#ef4444",
                      color: "white",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "10px",
                      fontWeight: "600",
                      zIndex: 1,
                    }}
                  >
                    {/* NOT COUNTED (DUPLICATE ORDER) */}
                  </div>
                )}
                <Req_Rec_Card
                  request={item}
                  rIndex={index}
                  setFinRequestsData={setAllFinanceData}
                  isRecord={item.type === "record"}
                  setRawData={setRawData}
                  selectedRequests={selectedRequests}
                  handleCheckboxChange={handleCheckboxChange}
                  className="finance-analytics-card"
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Helper functions for date ranges
const getDateRanges = (viewMode, startDate = new Date()) => {
  const ranges = [];
  const today = new Date(startDate);

  if (viewMode === "daily") {
    // Get last 30 days
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const start = new Date(date);
      start.setHours(0, 0, 0, 0);
      const end = new Date(date);
      end.setHours(23, 59, 59, 999);
      ranges.push([start, end]);
    }
  } else if (viewMode === "weekly") {
    // Get last 12 weeks
    for (let i = 0; i < 12; i++) {
      const start = new Date(today);
      start.setDate(start.getDate() - (start.getDay() + 7 * i));
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      ranges.push([start, end]);
    }
  } else if (viewMode === "monthly") {
    // Get last 12 months
    for (let i = 0; i < 12; i++) {
      const start = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const end = new Date(today.getFullYear(), today.getMonth() - i + 1, 0);
      end.setHours(23, 59, 59, 999);
      ranges.push([start, end]);
    }
  }

  return ranges;
};

const isDateInRange = (date, range) => {
  const checkDate = new Date(date);
  return checkDate >= range[0] && checkDate <= range[1];
};

const groupItemsByProject = (items) => {
  const projectGroups = {};

  items.forEach((item) => {
    let projectName = "Unknown Project";

    try {
      if (item.ProjectDetails) {
        const projectDetails = JSON.parse(item.ProjectDetails);
        if (
          typeof projectDetails === "object" &&
          Object.keys(projectDetails).length > 0
        ) {
          projectName = Object.values(projectDetails)[0];
        }
      }
    } catch (error) {
      console.error("Error parsing project details:", error);
    }

    if (!projectGroups[projectName]) {
      projectGroups[projectName] = [];
    }
    projectGroups[projectName].push(item);
  });

  return projectGroups;
};

const calculateSummary = (items) => {
  // Calculate total using business logic (OrderTotalAmount with timestamp threshold)
  const total = calculateOrderAmountWithBusinessLogic(items);

  let approved = 0;
  let pending = 0;
  let declined = 0;
  let requestCount = 0;
  let recordCount = 0;

  items.forEach((item) => {
    if (item.type === "request") {
      requestCount++;
      const amount = Number(item.AmountRequested || 0);

      if (item.status === "Approved") {
        approved += amount;
      } else if (item.status === "Request" || item.status === "Requested") {
        pending += amount;
      } else if (item.status === "Declined") {
        declined += amount;
      }
    } else if (item.type === "record") {
      recordCount++;
      const amount = Number(item.AmountPaid || 0);
      approved += amount;
    }
  });

  return {
    total,
    approved,
    pending,
    declined,
    requestCount,
    recordCount,
  };
};

const TimeBasedView = ({
  viewMode,
  allFinanceData,
  setAllFinanceData,
  setRawData,
  selectedRequests,
  handleCheckboxChange,
}) => {
  const [openTimeGroups, setOpenTimeGroups] = useState({});
  const [openProjectGroups, setOpenProjectGroups] = useState({});

  const dateRanges = useMemo(() => getDateRanges(viewMode), [viewMode]);

  const groupedData = useMemo(() => {
    const grouped = {};

    dateRanges.forEach((range, index) => {
      const rangeKey = `${range[0].toISOString()}-${range[1].toISOString()}`;
      grouped[rangeKey] = {
        range,
        items: allFinanceData.filter((item) => {
          let itemDate;
          if (item.type === "request") {
            itemDate = item.DOFReq ? new Date(item.DOFReq) : new Date();
          } else {
            itemDate = item.DateOfPayment
              ? new Date(item.DateOfPayment)
              : new Date();
          }
          return isDateInRange(itemDate, range);
        }),
        projects: [],
      };
    });

    // Group by projects within each time range
    Object.keys(grouped).forEach((rangeKey) => {
      if (grouped[rangeKey].items.length > 0) {
        const projectGroups = groupItemsByProject(grouped[rangeKey].items);
        grouped[rangeKey].projects = Object.keys(projectGroups);
        grouped[rangeKey].projectGroups = projectGroups;
      }
    });

    // Filter out empty ranges
    const filteredGrouped = {};
    Object.keys(grouped).forEach((key) => {
      if (grouped[key].items.length > 0) {
        filteredGrouped[key] = grouped[key];
      }
    });

    return filteredGrouped;
  }, [allFinanceData, dateRanges]);

  const calculateProjectSummary = (items) => {
    return calculateSummary(items);
  };

  const toggleTimeGroup = (key) => {
    setOpenTimeGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleProjectGroup = (timeKey, projectKey) => {
    const combinedKey = `${timeKey}-${projectKey}`;
    setOpenProjectGroups((prev) => ({
      ...prev,
      [combinedKey]: !prev[combinedKey],
    }));
  };

  return (
    <div>
      {Object.entries(groupedData).map(([rangeKey, data]) => {
        const summary = calculateSummary(data.items);
        const isTimeGroupOpen = openTimeGroups[rangeKey];

        return (
          <div key={rangeKey} style={{ marginBottom: "16px" }}>
            <TimeGroupHeader
              dateRange={data.range}
              isOpen={isTimeGroupOpen}
              toggleOpen={() => toggleTimeGroup(rangeKey)}
              summary={summary}
              projects={data.projects}
              projectGroups={data.projectGroups}
            />

            {isTimeGroupOpen && (
              <div
                style={{
                  backgroundColor: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "16px",
                  animation: "fadeIn 0.3s ease-in-out",
                }}
              >
                {Object.entries(data.projectGroups || {}).map(
                  ([projectName, projectItems]) => {
                    const projectSummary =
                      calculateProjectSummary(projectItems);
                    const combinedKey = `${rangeKey}-${projectName}`;
                    const isProjectOpen = openProjectGroups[combinedKey];

                    return (
                      <ProjectGroup
                        key={combinedKey}
                        projectName={projectName}
                        projectItems={projectItems}
                        isOpen={isProjectOpen}
                        toggleOpen={() =>
                          toggleProjectGroup(rangeKey, projectName)
                        }
                        setAllFinanceData={setAllFinanceData}
                        setRawData={setRawData}
                        selectedRequests={selectedRequests}
                        handleCheckboxChange={handleCheckboxChange}
                        summary={projectSummary}
                      />
                    );
                  }
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const filterData = (
  requestsArr,
  recordsArr,
  statusFilter,
  projectFilter,
  DateRangeValue,
  categoryFilter
) => {
  const filteredRequests = requestsArr.filter((item) => {
    // Status filter
    if (statusFilter !== "All") {
      if (statusFilter === "Request" && item.status !== "Request") return false;
      if (statusFilter !== "Request" && item.status !== statusFilter)
        return false;
    }

    // Project filter
    if (projectFilter !== "All") {
      try {
        const projectDetails = JSON.parse(item.ProjectDetails || "{}");
        const projectName = Object.values(projectDetails)[0];
        if (projectName !== projectFilter) return false;
      } catch {
        return false;
      }
    }

    // Category filter
    if (categoryFilter !== "All" && item.Category !== categoryFilter) {
      return false;
    }

    // Date filter
    if (DateRangeValue && DateRangeValue[0] && DateRangeValue[1]) {
      const itemDate = item.DOFReq || item.CreatedAt;
      if (!isDateLiesCheck(itemDate, DateRangeValue)) return false;
    }

    return true;
  });

  const filteredRecords = recordsArr.filter((item) => {
    // Status filter - records are generally approved
    if (statusFilter !== "All" && statusFilter !== "Approved") {
      return false;
    }

    // Project filter
    if (projectFilter !== "All") {
      try {
        const projectDetails = JSON.parse(item.ProjectDetails || "{}");
        const projectName = Object.values(projectDetails)[0];
        if (projectName !== projectFilter) return false;
      } catch {
        return false;
      }
    }

    // Category filter
    if (categoryFilter !== "All" && item.Category !== categoryFilter) {
      return false;
    }

    // Date filter
    if (DateRangeValue && DateRangeValue[0] && DateRangeValue[1]) {
      const itemDate = item.DateOfPayment || item.CreatedAt;
      if (!isDateLiesCheck(itemDate, DateRangeValue)) return false;
    }

    return true;
  });

  // Combine and mark type
  const combinedData = [
    ...filteredRequests.map((item) => ({ ...item, type: "request" })),
    ...filteredRecords.map((item) => ({ ...item, type: "record" })),
  ];

  return combinedData;
};

const DailyAnalytics = () => {
  const userContext = useContext(UserContext);
  const [isLoading, setLoading] = useState(true);
  const [allFinanceData, setAllFinanceData] = useState([]);
  const [rawRequestsData, setRawRequestsData] = useState([]);
  const [rawRecordsData, setRawRecordsData] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [DateRangeValue, setDateRangeValue] = useState([null, null]);
  const [selectedRequests, setSelectedRequests] = useState({});
  const [expandAll, setExpandAll] = useState(false);
  const [viewMode, setViewMode] = useState("daily");
  const [projectFilterName, setProjectFilterName] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [openDateGroups, setOpenDateGroups] = useState({});

  const getAllFinanceRecords = async () => {
    const opportunityService = new OpportunityService();

    try {
      // Use the single API that returns both requests and records
      const allData = await opportunityService.getAllFinanceRequest(
        userContext.user.Firm
      );

      if (allData.results && allData.results.length > 0) {
        // Separate requests and records based on properties
        const requests = [];
        const records = [];

        allData.results.forEach((item) => {
          if (item.AmountPaid !== undefined || item.DateOfPayment) {
            records.push(item);
          } else {
            requests.push(item);
          }
        });

        setRawRequestsData(requests);
        setRawRecordsData(records);
      } else {
        console.log("⚠️ No results in API response");
        setRawRequestsData([]);
        setRawRecordsData([]);
      }

      setLoading(false);
    } catch (error) {
      console.error("❌ Error fetching finance data:", error);
      setRawRequestsData([]);
      setRawRecordsData([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllFinanceRecords();
  }, []);

  useEffect(() => {
    if (rawRequestsData.length > 0 || rawRecordsData.length > 0) {
      const filteredData = filterData(
        rawRequestsData,
        rawRecordsData,
        statusFilter,
        projectFilterName,
        DateRangeValue,
        categoryFilter
      );

      console.log("✅ Filtered data:", filteredData.length, "items");

      // Initialize global order registry with the filtered data
      globalOrderRegistry.initialize(filteredData);

      setAllFinanceData(filteredData);
    } else {
      console.log("⚠️ No raw data available");
      // Reset registry when no data
      globalOrderRegistry.reset();
      setAllFinanceData([]);
    }
  }, [
    rawRequestsData,
    rawRecordsData,
    statusFilter,
    DateRangeValue,
    projectFilterName,
    categoryFilter,
  ]);

  // Group data by date
  const getGroupedData = useMemo(() => {
    const dateGroups = {};

    allFinanceData.forEach((item) => {
      let itemDate;

      if (item.type === "request") {
        if (item.DOFReq) {
          itemDate = new Date(item.DOFReq).toDateString();
        } else if (item.History) {
          try {
            const history = JSON.parse(item.History);
            const firstTimestamp = Object.keys(history)[0];
            itemDate = new Date(parseInt(firstTimestamp)).toDateString();
          } catch (error) {
            itemDate = new Date().toDateString();
          }
        } else {
          itemDate = new Date().toDateString();
        }
      } else {
        itemDate = item.DateOfPayment
          ? new Date(item.DateOfPayment).toDateString()
          : new Date().toDateString();
      }

      if (!dateGroups[itemDate]) {
        dateGroups[itemDate] = [];
      }
      dateGroups[itemDate].push(item);
    });

    return Object.entries(dateGroups).sort(
      ([dateA], [dateB]) => new Date(dateB) - new Date(dateA)
    );
  }, [allFinanceData]);

  // Initialize open state for date groups
  useEffect(() => {
    if (allFinanceData.length > 0 && getGroupedData.length > 0) {
      const newOpenState = {};
      getGroupedData.forEach(([date], index) => {
        newOpenState[date] = index === 0;
      });
      setOpenDateGroups(newOpenState);
    }
  }, [allFinanceData, getGroupedData]);

  const calculateTotalAmount = () => {
    return calculateOrderAmountWithBusinessLogic(allFinanceData);
  };

  const totalAmount = useMemo(() => calculateTotalAmount(), [allFinanceData]);

  const getProjectNamesFromData = () => {
    const projectNamesObj = [...rawRequestsData, ...rawRecordsData].reduce(
      (accumulator, item) => {
        try {
          const projectDetails = JSON.parse(item.ProjectDetails || "{}");
          if (
            typeof projectDetails === "object" &&
            Object.keys(projectDetails).length > 0
          ) {
            const projectName = Object.values(projectDetails)[0];
            accumulator[projectName] = true;
          }
        } catch (error) {
          console.error("Error parsing project details:", error);
        }
        return accumulator;
      },
      {}
    );

    return ["All", ...Object.keys(projectNamesObj || {})];
  };

  const projectNamesToFilter = useMemo(
    () => getProjectNamesFromData(),
    [rawRequestsData, rawRecordsData]
  );

  const getCategoryNamesFromData = () => {
    const categorySet = new Set();
    return [...rawRequestsData, ...rawRecordsData].reduce(
      (accumulator, item) => {
        const category = item.Category;
        if (!categorySet.has(category) && category) {
          categorySet.add(category);
          accumulator.push(category);
        }
        return accumulator;
      },
      ["All"]
    );
  };

  const categoryNamesToFilter = useMemo(
    () => getCategoryNamesFromData(),
    [rawRequestsData, rawRecordsData]
  );

  const handleCheckboxChange = (item) => {
    setSelectedRequests((prev) => {
      const newSelected = { ...prev };
      const key = item.uuid || item.id;

      if (newSelected[key]) {
        delete newSelected[key];
      } else {
        newSelected[key] = item;
      }

      return newSelected;
    });
  };

  const toggleAllDateGroups = () => {
    setExpandAll((prev) => !prev);
    const newOpenState = {};
    getGroupedData.forEach(([date]) => {
      newOpenState[date] = !expandAll;
    });
    setOpenDateGroups(newOpenState);
  };

  const toggleDateGroup = (date) => {
    setOpenDateGroups((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  return (
    <>
      {/* Filters */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          marginBottom: "20px",
          padding: "20px",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "15px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "15px",
              flexWrap: "wrap",
            }}
          >
            <DropDownMUI
              placeholder={"Project"}
              disabeSorting={true}
              data={projectNamesToFilter}
              value={projectFilterName}
              onChange={(value) => setProjectFilterName(value)}
              size={"small"}
              custStyles={{ minWidth: "140px" }}
            />
            <DropDownMUI
              placeholder={"Category"}
              disabeSorting={true}
              data={categoryNamesToFilter}
              value={categoryFilter}
              onChange={(value) => setCategoryFilter(value)}
              size={"small"}
              custStyles={{ minWidth: "130px" }}
            />
            <DropDownMUI
              placeholder={"Status"}
              disabeSorting={true}
              data={["All", "Requested", "Approved", "Declined", "On hold"]}
              value={statusFilter === "Request" ? "Requested" : statusFilter}
              onChange={(value) => {
                setStatusFilter(() => {
                  switch (value) {
                    case "Requested":
                      return "Request";
                    default:
                      return value;
                  }
                });
              }}
              size={"small"}
              custStyles={{ minWidth: "120px" }}
            />
            <div>
              <CustomDateRangePicker
                DateRange={DateRangeValue}
                setDateRange={setDateRangeValue}
              />
            </div>

            <TimeBubbles viewMode={viewMode} setViewMode={setViewMode} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <button
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                backgroundColor: "#f6f6f6",
                border: "1px solid #e0e0e0",
                padding: "6px 14px",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
                color: "#555",
                transition: "all 0.2s ease",
                minWidth: "110px",
                justifyContent: "center",
                whiteSpace: "nowrap",
              }}
              onClick={toggleAllDateGroups}
            >
              <span
                style={{
                  fontSize: "14px",
                  transform: expandAll ? "rotate(90deg)" : "rotate(0)",
                  display: "inline-block",
                  width: "14px",
                  height: "14px",
                  textAlign: "center",
                  lineHeight: "14px",
                  transition: "transform 0.3s ease",
                }}
              >
                ▶
              </span>
              {expandAll ? "Collapse All" : "Expand All"}
            </button>

            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: "#555",
                  whiteSpace: "nowrap",
                  fontWeight: "500",
                }}
              >
                Total Amount:
              </p>
              <p
                style={{
                  fontWeight: 700,
                  fontSize: "18px",
                  margin: 0,
                  color: "#333",
                  display: "flex",
                  alignItems: "center",
                  gap: "2px",
                }}
              >
                <span style={{ fontSize: "16px", marginRight: "2px" }}>₹</span>
                {totalAmount.toLocaleString("en-IN", {
                  maximumFractionDigits: 0,
                })}
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>

      {/* Content */}
      <div
        style={{
          height: "78vh",
          overflow: "auto",
          width: "100%",
          maxWidth: "100%",
          margin: "0",
          padding: "0",
          boxSizing: "border-box",
        }}
      >
        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "40px 0",
              width: "100%",
            }}
          >
            <CircularProgress
              style={{ width: "36px", height: "36px", color: "#fdd34d" }}
            />
          </div>
        ) : allFinanceData.length > 0 ? (
          viewMode === "daily" && !DateRangeValue[0] && !DateRangeValue[1] ? (
            // Original date-group view for daily mode without custom date range
            getGroupedData.map(([date, items]) => (
              <DateGroup
                key={date}
                date={date}
                items={items}
                isOpen={openDateGroups[date]}
                toggleOpen={() => toggleDateGroup(date)}
                setAllFinanceData={setAllFinanceData}
                setRawData={() => {}} // Combined setter not needed here
                selectedRequests={selectedRequests}
                handleCheckboxChange={handleCheckboxChange}
              />
            ))
          ) : (
            // Time-based view for weekly/monthly or when custom date range is selected
            <TimeBasedView
              viewMode={viewMode}
              allFinanceData={allFinanceData}
              setAllFinanceData={setAllFinanceData}
              setRawData={() => {}} // Combined setter not needed here
              selectedRequests={selectedRequests}
              handleCheckboxChange={handleCheckboxChange}
            />
          )
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px 0",
              color: "#666",
              backgroundColor: "#fffdf5",
              borderRadius: "8px",
              margin: "20px 0",
              border: "1px solid #fdd34d",
              width: "100%",
            }}
          >
            <h4 style={{ margin: 0 }}>
              No finance data matches your filter criteria
            </h4>
          </div>
        )}
      </div>
    </>
  );
};

export default DailyAnalytics;
